import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";

/**
 * Browser error monitoring (ADR-0013). Initialised only when VITE_SENTRY_DSN is
 * set at build time, so local dev without a DSN is a no-op.
 *
 * PII must never reach Sentry (CLAUDE.md rule 8): `sendDefaultPii` is off and
 * `scrubEvent` (exported for testing) strips query strings, request bodies in
 * breadcrumbs, and user identity beyond an opaque id before any event is sent.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) {
    event.request.url = event.request.url.split("?")[0];
  }
  if (event.user) {
    event.user = typeof event.user.id === "string" || typeof event.user.id === "number"
      ? { id: event.user.id }
      : {};
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data && typeof crumb.data === "object") {
        const data = crumb.data as Record<string, unknown>;
        if (typeof data.url === "string") data.url = data.url.split("?")[0];
        delete data.body;
        delete data.response_body;
      }
    }
  }
  if (event.extra) event.extra = {};
  return event;
}

export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0"),
    beforeSend: (event) => scrubEvent(event),
  });
}

export { Sentry };
